import QRCode from "qrcode";
import { ManualLink, ManualPeer, decodeSignal, type ManualConnection } from "yage/connection/ManualPeer";
import { startScanner, type Scanner } from "./qr-scanner.js";
import { randomCode } from "./lobby.js";

/**
 * Two-device play with no internet (a plane, a car): both devices join one Wi-Fi network or phone hotspot, then swap
 * QR codes. The host shows a code, the guest scans it and shows one back, and the host scans that. No server is used.
 */
export interface OfflinePlayOptions {
    /** Peer id prefix; it carries the game build so mismatched builds refuse each other. */
    prefix: string;
    preload(): Promise<void>;
    startHost(peer: ManualPeer): Promise<void>;
    startJoin(peer: ManualPeer, hostId: string): Promise<void>;
    /** A host code that arrived in the address (the guest scanned it with the phone's own camera app). */
    hostCode?: string | null;
}

/** Address fragment that carries a host code; fragments never reach a server. */
export const JOIN_HASH = "#offline=";

const OPEN_TIMEOUT_MS = 25000;
const NO_ROUTE = "The phones could not reach each other. Put both on the same hotspot or Wi-Fi, with the hotspot phone's Wi-Fi turned on, then try again.";

export function offlineHostCode(): string | null {
    return location.hash.startsWith(JOIN_HASH) ? decodeURIComponent(location.hash.slice(JOIN_HASH.length)) : null;
}

const css = (element: HTMLElement, style: Partial<CSSStyleDeclaration>) => Object.assign(element.style, style);
function el<K extends keyof HTMLElementTagNameMap>(tag: K, style: Partial<CSSStyleDeclaration> = {}, text = ""): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    css(node, style);
    if (text) node.textContent = text;
    return node;
}
function button(label: string, onClick: () => void, primary = true): HTMLButtonElement {
    const node = el("button", { minHeight: "52px", width: "100%", fontSize: "18px", fontWeight: "700", borderRadius: "10px", border: "0", cursor: "pointer",
        background: primary ? "#3fb68b" : "#24365a", color: "#ffffff", fontFamily: "inherit" }, label);
    node.type = "button";
    node.addEventListener("click", onClick);
    return node;
}
const withTimeout = <T,>(work: Promise<T>, ms: number, message: string) => Promise.race([work,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error(message)), ms))]);

/** Resolves once a game has started over the swapped link; rejects with an empty message when the player backs out. */
export function showOfflinePlay(options: OfflinePlayOptions): Promise<void> {
    return new Promise((resolve, reject) => {
        const overlay = el("div", { position: "fixed", inset: "0", zIndex: "1000", display: "grid", placeItems: "center", overflowY: "auto", padding: "16px",
            background: "#10213f", color: "#ffffff", fontFamily: "system-ui, sans-serif", boxSizing: "border-box" });
        const card = el("div", { width: "min(100%, 440px)", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", textAlign: "center" });
        overlay.append(card);
        document.body.append(overlay);
        let scanner: Scanner | null = null;
        let link: ManualLink | null = null;
        let finished = false;
        const cleanUp = () => { scanner?.stop(); scanner = null; };
        const close = () => { cleanUp(); overlay.remove(); };
        const cancel = () => { if (finished) return; finished = true; link?.close(); close(); reject(new Error("")); };
        const started = () => { finished = true; close(); resolve(); };

        const screen = (title: string, ...children: (HTMLElement | string)[]) => {
            card.replaceChildren(el("div", { fontSize: "13px", letterSpacing: "2px", color: "#a4e7ce" }, "PLAY OFFLINE"),
                el("div", { fontSize: "22px", fontWeight: "700" }, title),
                ...children.map(child => typeof child === "string" ? el("div", { fontSize: "15px", color: "#d4ece6", lineHeight: "1.4" }, child) : child));
        };
        const status = el("div", { minHeight: "20px", fontSize: "14px", color: "#a4e7ce" });
        const failed = (cause: unknown, retry: () => void) => {
            cleanUp();
            link?.close();
            link = null;
            const message = cause instanceof Error && cause.message ? cause.message : String(cause || "Something went wrong.");
            screen("Not connected", el("div", { fontSize: "15px", color: "#ff9c9c", lineHeight: "1.4" }, message), button("Try again", retry), button("Back", cancel, false));
        };
        const qrImage = async (text: string) => {
            const image = el("img", { width: "min(78vw, 300px)", aspectRatio: "1", background: "#ffffff", borderRadius: "8px", imageRendering: "pixelated" });
            image.alt = "Connection code";
            image.src = await QRCode.toDataURL(text, { errorCorrectionLevel: "L", margin: 3, width: 600 });
            return image;
        };
        const viewfinder = () => {
            const frame = el("div", { width: "min(60vw, 220px)", aspectRatio: "1", borderRadius: "10px", overflow: "hidden", background: "#000000", border: "3px solid #3fb68b" });
            if (scanner) { css(scanner.video, { width: "100%", height: "100%", objectFit: "cover" }); frame.append(scanner.video); }
            return frame;
        };
        const cameraError = (cause: unknown) => new Error(cause instanceof DOMException && cause.name === "NotAllowedError"
            ? "Camera permission is needed to scan codes. Allow the camera for this site and try again." : cause instanceof Error ? cause.message : "The camera could not start.");

        const host = async () => {
            screen("Hosting", "Starting the camera…", status);
            void options.preload().catch(() => {});
            const hostId = options.prefix + randomCode();
            let accepting = false;
            try {
                // Camera first: with it running the offer lists this phone's real network address.
                scanner = await startScanner(text => {
                    if (accepting || finished || !link) return;
                    let kind: string;
                    try { kind = decodeSignal(text).kind; } catch { return; }
                    if (kind !== "answer") { status.textContent = "That is your own code. Scan the code on the other phone."; return; }
                    accepting = true;
                    status.textContent = "Connecting…";
                    void (async () => {
                        try {
                            const connection = await withTimeout(link!.accept(text), OPEN_TIMEOUT_MS, NO_ROUTE);
                            await begin(() => {
                                const peer = new ManualPeer(hostId);
                                return options.startHost(peer).then(() => peer.accept(connection));
                            });
                        } catch (cause) { failed(cause, () => void host()); }
                    })();
                }).catch(cause => { throw cameraError(cause); });
                const offer = await ManualLink.offer(hostId);
                link = offer.link;
                const invite = location.origin + location.pathname + JOIN_HASH + encodeURIComponent(offer.code);
                screen("Hosting",
                    "1. On the other phone choose Play offline → Join, and scan this code.",
                    await qrImage(invite),
                    "2. Then point this phone's camera at the code on their screen.",
                    viewfinder(), status, button("Cancel", cancel, false));
            } catch (cause) { failed(cause, () => void host()); }
        };

        const join = async (known?: string | null) => {
            screen("Joining", known ? "Preparing your code…" : "Starting the camera…", status);
            void options.preload().catch(() => {});
            const guestId = options.prefix + randomCode();
            const answer = async (text: string) => {
                const offer = decodeSignal(text);
                if (offer.kind !== "offer") throw new Error("That is a guest code. Scan the code on the host's screen.");
                if (!offer.peerId.startsWith(options.prefix))
                    throw new Error("The two phones have different versions of the game. On both, connect to the internet, open the game and tap Save for offline, then try again.");
                const made = await ManualLink.answer(guestId, text);
                link = made.link;
                // The camera was only needed while the answer collected network addresses.
                cleanUp();
                screen("Joining", "Show this code to the host's camera.", await qrImage(made.code), status, button("Cancel", cancel, false));
                status.textContent = "Waiting for the host to scan…";
                const connection = await withTimeout(made.link.opened, 90000, NO_ROUTE);
                await begin(() => {
                    const peer = new ManualPeer(guestId);
                    peer.attach(connection);
                    return options.startJoin(peer, offer.peerId);
                });
            };
            try {
                if (known) {
                    // Opened from the phone's camera app: no in-page scan needed. (Waiting on a camera prompt here
                    // left a blank screen when the prompt never showed.)
                    await answer(known);
                    return;
                }
                let answering = false;
                scanner = await startScanner(text => {
                    if (answering || finished) return;
                    try { decodeSignal(text); } catch { return; }
                    answering = true;
                    status.textContent = "Code found…";
                    answer(text).catch(cause => failed(cause, () => void join()));
                }).catch(cause => { throw cameraError(cause); });
                screen("Joining", "Point this phone's camera at the code on the host's screen.", viewfinder(), status, button("Cancel", cancel, false));
            } catch (cause) { failed(cause, () => void join()); }
        };

        const begin = async (start: () => Promise<void>) => {
            cleanUp();
            screen("Connected!", "Loading the airport… this can take up to a minute on a phone.");
            await start();
            if (location.hash.startsWith(JOIN_HASH)) history.replaceState(null, "", location.pathname + location.search);
            started();
        };

        if (options.hostCode) { void join(options.hostCode); return; }
        screen("Two phones, no internet",
            "Put both phones on the same Wi-Fi or turn on one phone's hotspot and join it from the other. Mobile data is not needed.",
            button("Host the game", () => void host()),
            button("Join the game", () => void join()),
            button("Back", cancel, false));
    });
}
